<!--

    This component represents a single Header. If it's clicked, it will change the sorting to the target
    option. If clicked again, it will change the sort order.

    The headers are bydefault resizable, which means that the user can define the size of each column. The resize will
    trigger a rerender of the whole table.

    More details at the interface: TableFilterOption
-->
<script lang="ts">
	import Icon from '../../Icon.svelte';
	import { SortingOrder, type FilterOption, type SimpleListListFilter } from '../../../types/ListTypes';

	interface Props {
		header: FilterOption;
		simpleListListFilter: SimpleListListFilter;
		onHeaderResized: () => void;
		onSort: (header: FilterOption) => void;
	}

	let { header = $bindable(), simpleListListFilter, onHeaderResized, onSort }: Props = $props();

	//Bind the html elements to get their props.
	let button: HTMLButtonElement | undefined = $state();
	let resizer: HTMLButtonElement | undefined = $state();

	//Function for moving the table header width in a throttled way.
	let moveFn: (e: any) => void;

	let lastClickPos: number = 0;

	let lockClick: boolean = false;

	function getXCoordinate(e: MouseEvent | TouchEvent) {
		if (e instanceof MouseEvent) {
			return Math.floor(e.clientX);
		} else {
			return Math.floor(e.changedTouches[0].clientX);
		}
	}

	export const throttle = (fn: (...args: any[]) => void, wait = 100) => {
		let inThrottle: boolean;
		let lastFn: ReturnType<typeof setTimeout>;
		let lastTime: number;

		return (...args: any[]) => {
			if (!inThrottle) {
				fn(...args);
				lastTime = Date.now();
				inThrottle = true;
			} else {
				clearTimeout(lastFn);
				lastFn = setTimeout(
					() => {
						if (Date.now() - lastTime >= wait) {
							fn(...args);
							lastTime = Date.now();
						}
					},
					Math.max(wait - (Date.now() - lastTime), 0)
				);
			}
		};
	};

	function startResize(e: MouseEvent | TouchEvent) {
		e.stopPropagation();

		resizer?.classList.add('active');

		//Remember the offset
		lastClickPos = getXCoordinate(e);

		//Lock the own click
		lockClick = true;

		moveFn = throttle((e) => move(e), 10);

		document.addEventListener('mouseup', end);
		document.addEventListener('touchend', end);
		document.addEventListener('mousemove', moveFn);
		document.addEventListener('touchmove', moveFn);
	}

	function move(e: MouseEvent | TouchEvent) {
		e.stopPropagation();

		if (!button) return;

		//Calc the move distance of the mouse
		const movedDist = lastClickPos - getXCoordinate(e);

		//Check if we have the table option set
		if (header.tableOption) {
			const width = header.tableOption?.width || -1;
			//Calc the new width
			if (width <= 0) {
				header.tableOption.width = Math.max(button.offsetWidth - movedDist, 40);
			} else {
				header.tableOption.width = Math.max(width - movedDist, 40);
			}
		} else {
			header.tableOption = {
				width: Math.max(button.offsetWidth - movedDist, 40)
			};
		}

		lastClickPos = getXCoordinate(e);
	}

	function end(e: MouseEvent | TouchEvent) {
		e.stopPropagation();

		//Remove the lock
		lockClick = false;

		//Remove the listener again so we do not get spam
		document.removeEventListener('mouseup', end);
		document.removeEventListener('touchend', end);
		document.removeEventListener('mousemove', moveFn);
		document.removeEventListener('touchmove', moveFn);

		//Remove the style class
		resizer?.classList.remove('active');

		//Make sure the table get's the event.
		onHeaderResized();
	}

	function handleOnClick() {
		if (!lockClick && header.isSorting != false) {
			onSort(header);
		}
	}

	function handleSetToInfinit() {
		header.tableOption = { width: -1 };
		//Make sure the table get's the event.
		onHeaderResized();
	}
</script>

<button
	style={header.tableOption?.width ? `width: ${header.tableOption?.width}px;` : ''}
	bind:this={button}
	onclick={handleOnClick}
	class="p-2 text-sm font-bold uppercase text-darkgray dark:text-white border-gray dark:border-dark-200 relative border-r-2 border-b-2"
	class:cursor-default={header.isSorting == false}
>
	<div class="flex">
		<p class="grow {header.tableOption?.alignClass || 'text-left'} truncate">
			{header.label}
		</p>

		<!--Show the sorting icon when the key matches.-->
		{#if simpleListListFilter.sortingKey == (header.key ? header.key : '')}
			{#if simpleListListFilter.sortingOrder === SortingOrder.ASC}
				<Icon name="sort-up" classInfo="inline fill-gray dark:fill-secondary-500" />
			{/if}
			{#if simpleListListFilter.sortingOrder === SortingOrder.DESC}
				<Icon name="sort-down" classInfo="inline fill-gray dark:fill-secondary-500" />
			{/if}
		{/if}
	</div>

	<!--Resizer-->
	{#if header.tableOption?.resizable != null ? header.tableOption?.resizable : true}
		<!-- svelte-ignore a11y_consider_explicit_label -->
		<!-- svelte-ignore node_invalid_placement_ssr -->
		<button
			aria-label="resize"
			bind:this={resizer}
			class="absolute top-0 right-0 bottom-0 w-2 rounded-l-lg cursor-col-resize hover:bg-secondary-300 dark:hover:bg-secondary-500"
			onmousedown={startResize}
			ontouchstart={startResize}
			ondblclick={handleSetToInfinit}
			onclick={(e) => {
				e.stopPropagation();
			}}
		></button>
	{/if}
</button>
